import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Search,
  Ban,
  Eye,
  RefreshCw,
  FileSpreadsheet,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { Avatar } from '../../ui/Avatar';
import ExportConfirmModal from '../../ui/ExportConfirmModal';
import { usePlayerFilterMeta, useUsers } from '../../../hooks/useApi';
import { User, UserFilters } from '../../../types';
import { format } from 'date-fns';
import { cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { exportToExcel } from '../../../utils/exportExcel';
import BanUserModal from './BanUserModal';
import UpdateUserStatusModal from './UpdateUserStatusModal';
import SignupBonusDetailModal from './SignupBonusDetailModal';
import type { SignupBonusGrant, SignupBonusStatus } from '../../../types';
import { useAuth, usePermission } from '../../../hooks/useAuth';
import { apiClient } from '../../../services/api';
import { toast } from 'react-hot-toast';
import { NumberedPagination, SortableTableHead } from '@kioskgaming/ui';
import useTableSort, { resolveHeaderSort } from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import {
  AdminTableColgroup,
  ADMIN_CLIP_CELL,
  ADMIN_RAW_CELL,
  ADMIN_TABLE_FIXED_CLASS,
  adminCellClass,
  PLAYERS_LIST_COLUMNS,
  PL,
} from '../../../config/adminTableLayout';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';

// KIOSK-317 — full signup-bonus badge for every state (not only active).
/** Ops labels match API statuses (inactive → active → in_review → completed | expired). */
const BONUS_BADGE: Record<SignupBonusStatus, { label: string; className: string }> = {
  inactive: { label: 'Inactive', className: 'bg-violet-500/15 text-violet-800 ring-violet-500/25 hover:bg-violet-500/25' },
  active: { label: 'Active', className: 'bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 hover:bg-emerald-500/25' },
  in_review: { label: 'In review', className: 'bg-amber-500/15 text-amber-900 ring-amber-500/25 hover:bg-amber-500/25' },
  completed: { label: 'Completed', className: 'bg-sky-500/15 text-sky-800 ring-sky-500/25 hover:bg-sky-500/25' },
  expired: { label: 'Expired', className: 'bg-slate-500/10 text-slate-700 ring-slate-500/20 hover:bg-slate-500/20' },
};

const BONUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'none', label: 'None' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'active', label: 'Active' },
  { value: 'in_review', label: 'In review' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

const BASE_SELECT_CLASS =
  'w-full h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60';

function filterSelectClass(active: boolean, tint: 'emerald' | 'sky' | 'violet' | 'amber') {
  const tintClass = {
    emerald: 'border-emerald-300/80 bg-emerald-50/50',
    sky: 'border-sky-300/80 bg-sky-50/50',
    violet: 'border-violet-300/80 bg-violet-50/50',
    amber: 'border-amber-300/80 bg-amber-50/50',
  }[tint];
  return cn(BASE_SELECT_CLASS, active && tintClass);
}

interface UserListProps {
  filters: UserFilters;
  setFilters: React.Dispatch<React.SetStateAction<UserFilters>>;
}

const UserList: React.FC<UserListProps> = ({
  filters,
  setFilters
}) => {
  const navigate = useNavigate();
  const fmt = useFormatDate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    if (!searchParams.get('sort')) {
      const params = new URLSearchParams(searchParams);
      params.set('sort', 'created_at');
      params.set('dir', 'desc');
      setSearchParams(params, { replace: true });
    }
    // Seed default sort indicator once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((prev) => {
        const nextSearch = searchInput.trim() || undefined;
        if ((prev.search || undefined) === nextSearch) return prev;
        return { ...prev, search: nextSearch, page: 1 };
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, setFilters]);

  const tableSort = useTableSort({
    columns: getTableSortColumns('players'),
    onChange: () => {
      setFilters((prev) => ({ ...prev, page: 1 }));
    },
  });

  useEffect(() => {
    setFilters((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: UserFilters = { ...prev, page: 1 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as UserFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder, setFilters]);

  const { data: usersData, isLoading, isFetching, error, refetch } = useUsers(filters);
  const { data: filterMetaRes, isLoading: filterMetaLoading } = usePlayerFilterMeta();
  const agents = filterMetaRes?.data?.agents ?? [];
  const shopsAll = useMemo(
    () => filterMetaRes?.data?.shops ?? [],
    [filterMetaRes?.data?.shops]
  );
  const shopsForSelect = useMemo(() => {
    if (!filters.agent_id) return shopsAll;
    return shopsAll.filter((s) => s.agentId === filters.agent_id);
  }, [shopsAll, filters.agent_id]);
  const [banModalUser, setBanModalUser] = useState<User | null>(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [statusModalUser, setStatusModalUser] = useState<User | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState<SignupBonusGrant | null>(null);
  const { user: currentAdmin } = useAuth();
  // KIOSK-720: support_admin is view-only for Manage Cashiers/Players — cannot ban/unban.
  const canManageUsers = usePermission('userManagement') && currentAdmin?.role !== 'support_admin';
  // KIOSK-720: Export Data = No for support_admin (permission matrix).
  const canExport = currentAdmin?.role !== 'support_admin';

  const EXPORT_LIMIT = 1000;

  const handleExportConfirm = async () => {
    setIsExporting(true);
    try {
      const exportFilters: UserFilters = {
        ...filters,
        page: 1,
        limit: EXPORT_LIMIT,
      };
      const res = await apiClient.getUsers(exportFilters);
      const usersToExport = res?.data?.users ?? [];
      const rows = usersToExport.map((u: User) => ({
        Username: u.username?.trim() || u.userName?.trim() || '',
        Email: u.email ?? '',
        Phone: u.phone ?? '',
        Agent: u.shop?.agent?.name ?? '',
        Shop: u.shop?.name ?? '',
        Status: u.status ?? '',
        'Bonus status': u.signupBonus
          ? (BONUS_BADGE[u.signupBonus.status]?.label || u.signupBonus.status)
          : 'None',
        Banned: u.banned ? 'Yes' : 'No',
        'Created at': fmt(u.created_at),
        'Last activity at': u.last_login_at ? fmt(u.last_login_at) : 'Never',
      }));
      exportToExcel(rows, {
        sheetName: 'Players',
        fileName: `players-export-${format(new Date(), 'yyyy-MM-dd')}`,
      });
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (err) {
      console.error('Export users error:', err);
      toast.error('Failed to export players. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleBanUser = (user: User) => {
    setBanModalUser(user);
    setShowBanModal(true);
  };

  const handleBanSuccess = () => {
    refetch();
  };

  const handleStatusSuccess = () => {
    refetch();
  };

  const handleStatusFilter = (status: User['status'] | undefined) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handleBonusStatusFilter = (value: string) => {
    const bonus_status =
      value === 'all' ? undefined : (value as UserFilters['bonus_status']);
    setFilters((prev) => ({ ...prev, bonus_status, page: 1 }));
  };

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setFilters((prev) => ({ ...prev, search: undefined, page: 1 }));
  }, [setFilters]);

  const handleAgentFilter = (value: string) => {
    const agent_id = value === 'all' ? undefined : value;
    setFilters((prev) => {
      let shop_id = prev.shop_id;
      if (agent_id && shop_id) {
        const ok = shopsAll.some((s) => s.id === shop_id && s.agentId === agent_id);
        if (!ok) shop_id = undefined;
      }
      return { ...prev, agent_id, shop_id, page: 1 };
    });
  };

  const handleShopFilter = (value: string) => {
    const shop_id = value === 'all' ? undefined : value;
    setFilters((prev) => ({ ...prev, shop_id, page: 1 }));
  };

  const getStatusBadge = (status: User['status']) => {
    const statusConfig: Record<User['status'], { label: string; className: string }> = {
      active: { label: 'Active', className: 'bg-emerald-500/15 text-emerald-800 ring-1 ring-emerald-500/25' },
      inactive: { label: 'Inactive', className: 'bg-slate-500/10 text-slate-800 ring-1 ring-slate-500/20' },
      suspended: { label: 'Suspended', className: 'bg-amber-500/15 text-amber-900 ring-1 ring-amber-500/25' },
      banned: { label: 'Banned', className: 'bg-red-500/10 text-red-800 ring-1 ring-red-500/20' },
      terminated: { label: 'Closed', className: 'bg-neutral-500/10 text-neutral-800 ring-1 ring-neutral-500/20' },
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium', config.className)}>
        {config.label}
      </span>
    );
  };

  const primaryIdentifier = (user: User) => user.phone?.trim() || user.email?.trim() || '—';
  const secondaryIdentifier = (user: User) => {
    const phone = user.phone?.trim();
    const email = user.email?.trim();
    if (phone && email) return email;
    return null;
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Failed to load players list</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const users = usersData?.data?.users || [];
  const pagination = usersData?.data?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  };

  const headerSort = resolveHeaderSort(tableSort, {
    sortBy: pagination.sortBy ?? filters.sortBy ?? null,
    sortOrder: (pagination.sortOrder ?? filters.sortOrder ?? null) as SortDirection | null,
  });

  const isTableBusy = isFetching && users.length > 0;

  const hasActiveFilters =
    Boolean(filters.search) ||
    Boolean(filters.status) ||
    Boolean(filters.bonus_status) ||
    Boolean(filters.agent_id) ||
    Boolean(filters.shop_id);

  return (
    <Card className="overflow-hidden border-border/80 shadow-sm">
      <CardHeader className="border-b border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 via-white to-violet-50/60 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center text-lg font-semibold tracking-tight">
            <Users className="mr-2 h-5 w-5 text-indigo-600" />
            Players
            <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-sm font-normal text-indigo-800 tabular-nums">
              {pagination.total || 0}
            </span>
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {canExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={users.length === 0}
                className="border-border/80"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => refetch()} className="border-border/80">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Filters — compact toolbar */}
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <div className="min-w-0 flex-1 lg:max-w-xs">
            <label htmlFor="player-search" className="mb-1 block text-xs font-medium text-muted-foreground">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="player-search"
                placeholder="Username, email, or phone…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={cn(
                  'h-9 pl-9 pr-9',
                  searchInput && 'border-indigo-300/80 ring-1 ring-indigo-200/60 focus-visible:ring-indigo-300',
                )}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-1 lg:flex-wrap lg:items-end lg:gap-3">
            <div className="min-w-[140px] sm:min-w-0 lg:w-36">
              <label htmlFor="player-status" className="mb-1 block text-xs font-medium text-muted-foreground">
                Status
              </label>
              <select
                id="player-status"
                value={filters.status || 'all'}
                onChange={(e) => handleStatusFilter(e.target.value === 'all' ? undefined : e.target.value as User['status'])}
                className={filterSelectClass(Boolean(filters.status), 'emerald')}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="banned">Banned</option>
                <option value="terminated">Closed</option>
              </select>
            </div>
            <div className="min-w-[140px] sm:min-w-0 lg:w-40">
              <label htmlFor="player-bonus-status" className="mb-1 block text-xs font-medium text-muted-foreground">
                Bonus status
              </label>
              <select
                id="player-bonus-status"
                value={filters.bonus_status || 'all'}
                onChange={(e) => handleBonusStatusFilter(e.target.value)}
                className={filterSelectClass(Boolean(filters.bonus_status), 'amber')}
              >
                {BONUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px] sm:min-w-0 lg:w-40">
              <label htmlFor="player-agent" className="mb-1 block text-xs font-medium text-muted-foreground">
                Agent
              </label>
              <select
                id="player-agent"
                value={filters.agent_id || 'all'}
                onChange={(e) => handleAgentFilter(e.target.value)}
                disabled={filterMetaLoading}
                className={filterSelectClass(Boolean(filters.agent_id), 'sky')}
              >
                <option value="all">All agents</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px] sm:min-w-0 lg:min-w-[12rem] lg:flex-1">
              <label htmlFor="player-shop" className="mb-1 block text-xs font-medium text-muted-foreground">
                Shop
              </label>
              <select
                id="player-shop"
                value={filters.shop_id || 'all'}
                onChange={(e) => handleShopFilter(e.target.value)}
                disabled={filterMetaLoading}
                className={filterSelectClass(Boolean(filters.shop_id), 'violet')}
              >
                <option value="all">All shops</option>
                {shopsForSelect.map((s) => (
                  <option key={s.id} value={s.id}>
                    {filters.agent_id ? s.name : `${s.name}${s.agentName ? ` (${s.agentName})` : ''}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* User table */}
        {isLoading && users.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : users.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No players found.
            {hasActiveFilters && ' Try clearing filters to see more results.'}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/60">
            <table className={cn(ADMIN_TABLE_FIXED_CLASS, 'border-collapse text-sm')} aria-busy={isTableBusy || undefined}>
              <AdminTableColgroup columns={PLAYERS_LIST_COLUMNS} />
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}>
                    Username
                  </th>
                  <th className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}>
                    Email & phone
                  </th>
                  <th className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}>
                    Agent & shop
                  </th>
                  <SortableTableHead
                    sortable
                    className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}
                    label="Status"
                    sortKey="status"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <th className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}>
                    Bonus status
                  </th>
                  <SortableTableHead
                    sortable
                    className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}
                    label="Created at"
                    sortKey="created_at"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <SortableTableHead
                    sortable
                    className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-xs font-medium uppercase tracking-wide text-muted-foreground')}
                    label="Last activity at"
                    sortKey="last_login_at"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <th className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-right text-xs font-medium uppercase tracking-wide text-muted-foreground')}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                {users.map((user, rowIndex) => {
                  const agentName = user.shop?.agent?.name?.trim() || '—';
                  const shopName = user.shop?.name?.trim() || '—';
                  const username = user.username?.trim() || user.userName?.trim() || '—';
                  const agentId = user.shop?.agent?.id;
                  const shopId = user.shop?.id;
                  const isPlatformPlayer = !user.shop_id && !shopId;
                  const sec = secondaryIdentifier(user);
                  const agentLinkClass = 'block truncate font-medium text-sky-700 underline-offset-2 hover:underline';
                  const shopLinkClass = 'mt-0.5 block truncate text-xs font-normal text-violet-600 underline-offset-2 hover:underline';
                  return (
                    <tr
                      key={user.id}
                      className={cn(
                        'border-b border-border/40 transition-colors last:border-0',
                        rowIndex % 2 === 1 && 'bg-slate-50/30',
                        'hover:bg-indigo-50/40',
                      )}
                    >
                      <td className={cn(ADMIN_RAW_CELL, ADMIN_CLIP_CELL, 'font-medium text-foreground', adminCellClass(PLAYERS_LIST_COLUMNS[PL.username]))}>
                        <span className="block w-full truncate" title={username !== '—' ? username : undefined}>
                          {username}
                        </span>
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, ADMIN_CLIP_CELL, adminCellClass(PLAYERS_LIST_COLUMNS[PL.emailPhone]))}>
                        <div className="flex min-w-0 items-center gap-2">
                          <Avatar user={user} size="sm" deterministicColor className="shrink-0" />
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <div className="truncate font-medium text-foreground" title={primaryIdentifier(user)}>
                              {primaryIdentifier(user)}
                            </div>
                            {sec && (
                              <div className="truncate text-xs text-muted-foreground" title={sec}>
                                {sec}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, ADMIN_CLIP_CELL, adminCellClass(PLAYERS_LIST_COLUMNS[PL.agentShop]))}>
                        <div className="min-w-0">
                          {isPlatformPlayer ? (
                            <span className="inline-flex rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                              Platform
                            </span>
                          ) : (
                            <>
                              {agentId ? (
                                <Link to={`/agents/${agentId}`} className={agentLinkClass}>
                                  {agentName}
                                </Link>
                              ) : (
                                <div className="truncate font-medium text-foreground">{agentName}</div>
                              )}
                              {shopId && agentId ? (
                                <Link to={`/shops/${shopId}`} className={shopLinkClass}>
                                  {shopName}
                                </Link>
                              ) : (
                                <div className="mt-0.5 truncate text-xs text-muted-foreground">{shopName}</div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap', adminCellClass(PLAYERS_LIST_COLUMNS[PL.status]))}>
                        {user.banned ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-800 ring-1 ring-red-500/20">
                            <Ban className="h-3 w-3" />
                            Banned
                          </span>
                        ) : (
                          getStatusBadge(user.status)
                        )}
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap', adminCellClass(PLAYERS_LIST_COLUMNS[PL.bonus]))}>
                        {user.signupBonus ? (
                          <button
                            type="button"
                            onClick={() => setSelectedBonus(user.signupBonus!)}
                            className={cn(
                              'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 transition-colors',
                              BONUS_BADGE[user.signupBonus.status]?.className || BONUS_BADGE.expired.className
                            )}
                          >
                            {BONUS_BADGE[user.signupBonus.status]?.label || user.signupBonus.status}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap text-slate-600 tabular-nums', adminCellClass(PLAYERS_LIST_COLUMNS[PL.createdAt]))}>
                        {fmt(user.created_at, { second: undefined })}
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, 'whitespace-nowrap tabular-nums', adminCellClass(PLAYERS_LIST_COLUMNS[PL.lastActivity]))}>
                        {user.last_login_at ? (
                          <span className="font-medium text-teal-700">
                            {fmt(user.last_login_at, { second: undefined })}
                          </span>
                        ) : (
                          <span className="italic text-slate-400">Never</span>
                        )}
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(PLAYERS_LIST_COLUMNS[PL.actions]))}>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                            onClick={() => navigate(`/users/${user.id}`)}
                            aria-label="View player"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canManageUsers && (
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                'h-8 w-8 shrink-0 border-0 p-0',
                                user.banned
                                  ? 'text-primary hover:bg-primary/10'
                                  : 'text-destructive hover:bg-destructive/10'
                              )}
                              onClick={() => handleBanUser(user)}
                              aria-label={user.banned ? 'Unban player' : 'Ban player'}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} players
            </p>
            <NumberedPagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
              variant="admin"
            />
          </div>
        )}
      </CardContent>

      {/* Ban User Modal */}
      {canManageUsers && (
        <BanUserModal
          user={banModalUser}
          isOpen={showBanModal}
          onClose={() => {
            setShowBanModal(false);
            setBanModalUser(null);
          }}
          onSuccess={handleBanSuccess}
        />
      )}

      {/* Update Status Modal */}
      <UpdateUserStatusModal
        user={statusModalUser}
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setStatusModalUser(null);
        }}
        onSuccess={handleStatusSuccess}
      />

      {/* Export Confirm Modal */}
      <ExportConfirmModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        isExporting={isExporting}
        title="Export to Excel"
        message="Only the latest 1000 records will be exported. Do you want to continue?"
      />

      <SignupBonusDetailModal
        bonus={selectedBonus}
        isOpen={selectedBonus !== null}
        onClose={() => setSelectedBonus(null)}
      />
    </Card>
  );
};

export { UserList };
